import { FC } from "react";
import classNames from "classnames";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";
import Carrier from "@Components/Carrier/Carrier";
import { DownIcon } from "src/assets/Icons";
import type { PassengerTabItem, PassengerTabsRenderProps } from "../PassengerTabs";
import PassengerStatusBadge from "../components/PassengerStatusBadge";

type PassengerGroup = { key: string; label?: string; entries: { item: PassengerTabItem; index: number }[] };

const arrowClassName =
  "border-border-default flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-opacity disabled:cursor-not-allowed disabled:opacity-30";

const PassengerTabsMob: FC<PassengerTabsRenderProps> = ({ items, value, onChange, labels }) => {
  const { t } = useTranslation();
  const activeIndex = Math.max(
    items.findIndex((item) => item.value === value),
    0,
  );
  const activeItem = items[activeIndex];
  const completedCount = items.filter((item) => item.isComplete).length;
  const groups = items.reduce<PassengerGroup[]>((acc, item, index) => {
    const key = String(item.groupKey ?? "");
    const group = acc.find((entry) => entry.key === key) ?? acc[acc.push({ key, label: item.groupLabel, entries: [] }) - 1];
    group.entries.push({ item, index });
    return acc;
  }, []);

  const goTo = (index: number) => {
    const target = items[index];
    if (target) onChange(target.value);
  };

  return (
    <Carrier
      label={
        <div className="flex flex-col">
          <span className="text-lg font-semibold">{labels.title}</span>
          <span className="text-font-subtle text-xs font-medium">{labels.completed(completedCount, items.length)}</span>
        </div>
      }
      renderTrigger={({ open }) => (
        <div className="bg-background-default flex flex-col gap-3 rounded-md p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <button type="button" onClick={open} className="flex min-w-0 flex-1 cursor-pointer flex-col items-center">
              <span className="text-font-subtle text-xs font-medium">{labels.progress(activeIndex + 1, items.length)}</span>
              <span className="text-font-default flex max-w-full items-center gap-1.5 text-base font-semibold">
                <span className="truncate">{activeItem?.name}</span>
                {activeItem?.isLead && (
                  <span className="bg-background-primary text-font-inverted flex h-4 w-4 shrink-0 items-center justify-center rounded-full">
                    <MdCheck className="h-3 w-3" />
                  </span>
                )}
                <DownIcon className="text-font-subtle h-3 w-3 shrink-0" />
              </span>
            </button>
          </div>
        </div>
      )}
      renderSheet={({ close }) => (
        <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-6 p-4">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-2">
              {group.label && (
                <div className="text-font-subtle text-xs font-semibold uppercase tracking-[0.5px]">{group.label}</div>
              )}
              {group.entries.map(({ item, index }) => {
                const isActive = item.value === value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      onChange(item.value);
                      close();
                    }}
                    className={classNames(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 text-start transition-colors",
                      isActive ? "border-border-active bg-background-primary/5" : "border-border-subtle",
                    )}
                  >
                    <PassengerStatusBadge item={item} index={index} isActive={isActive} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-font-default truncate text-base font-semibold">{item.name}</span>
                      <span className="text-font-subtle text-xs">{item.typeLabel}</span>
                    </span>
                    {item.isLead && (
                      <div className="bg-background-primary text-font-inverted shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium">
                        {labels.lead}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    />
  );
};

export default PassengerTabsMob;
